import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { AuthProvider } from './auth/AuthProvider';
import { ToastProvider } from './components/Toasts';
import { PreferencesProvider } from './lib/PreferencesProvider';
import { initI18n } from './i18n';
import { applyPreferences, resolveLanguage, resolveTheme } from './lib/preferences';

/*
 * The prototype's stylesheet, vendored unchanged, then our additions. Order
 * matters: additions.css layers on top.
 */
import './styles/prototype.css';
import './styles/additions.css';

/**
 * Resolve and apply language, direction and theme *before* the first render, so
 * the page never paints left-to-right in the light theme and then flips.
 */
const language = resolveLanguage(null);
const theme = resolveTheme(null);
applyPreferences({ language, theme });
initI18n(language);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // A 403 or 404 is a settled answer, not a transient fault. Retrying it
      // wastes a round trip and delays showing the user what happened.
      retry: (failureCount, error: unknown) => {
        const status = (error as { status?: number } | null)?.status;
        if (status !== undefined && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root is missing from index.html.');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/*
        Preferences sit outside the router and outside auth, so the theme and
        language controls exist on the sign-in screen too — not only once a
        session is established.
      */}
      <PreferencesProvider>
        <BrowserRouter>
          <AuthProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      </PreferencesProvider>
    </QueryClientProvider>
  </StrictMode>,
);
